import {
  type PlatformInsightsOnboardingDistrictDto,
  usePlatformInsightsGetDistrictOnboarding,
} from "@crowdcoursing/api/openapi";
import { Link as LinkIcon } from "@mui/icons-material";
import { Box, Chip, Link as MuiLink, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { type ColumnData, type RowData } from "~types/datagrid";
import { DataGrid } from "~ui/molecules/DataGrid/DataGrid";

import { DistrictOnboardingDetailDrawer, type DistrictOnboardingDetailMetric } from "./DistrictOnboardingDetailDrawer";
import { buildDistrictOnboardingGridRows, type DistrictOnboardingGridRow } from "./districtOnboardingMetrics";

type GridRow = RowData<DistrictOnboardingGridRow>;

const statusColor = (
  status: DistrictOnboardingGridRow["status"],
): "default" | "error" | "info" | "success" | "warning" => {
  switch (status) {
    case "Launched":
      return "success";
    case "Not started":
      return "default";
    case "Onboarding":
      return "info";
    case "Starting":
      return "warning";
    default:
      return "default";
  }
};

const countButtonSx = {
  "&:hover": { textDecoration: "underline" },
  background: "none",
  border: 0,
  color: "primary.main",
  cursor: "pointer",
  font: "inherit",
  p: 0,
  textAlign: "inherit" as const,
} as const;

function makeCountColumn(
  field: "peopleOnboardedCount" | "schoolCount" | "schoolsOnboardedCount",
  headerName: string,
  minWidth: number,
  metric: DistrictOnboardingDetailMetric,
  openDetail: (district: PlatformInsightsOnboardingDistrictDto, m: DistrictOnboardingDetailMetric) => void,
): ColumnData<DistrictOnboardingGridRow> {
  return {
    align: "right",
    field,
    headerAlign: "right",
    headerName,
    minWidth,
    renderCell: ({ row }) => (
      <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
        <Typography
          component="button"
          onClick={(e) => {
            e.stopPropagation();
            openDetail(row.source, metric);
          }}
          sx={countButtonSx}
          type="button"
          variant="body2"
        >
          {row[field]}
        </Typography>
      </Box>
    ),
    sortable: true,
  };
}

export const DistrictOnboardingPanel = () => {
  const { data, status } = usePlatformInsightsGetDistrictOnboarding({});

  const [detail, setDetail] = useState<{
    district: PlatformInsightsOnboardingDistrictDto;
    metric: DistrictOnboardingDetailMetric;
  } | null>(null);

  const openDetail = useCallback(
    (district: PlatformInsightsOnboardingDistrictDto, metric: DistrictOnboardingDetailMetric) => {
      setDetail({ district, metric });
    },
    [],
  );

  const closeDetail = useCallback(() => {
    setDetail(null);
  }, []);

  const columns = useMemo((): ColumnData<DistrictOnboardingGridRow>[] => {
    return [
      {
        field: "districtName",
        flex: 1.2,
        headerName: "District",
        minWidth: 200,
        renderCell: ({ row }) => (
          <MuiLink
            aria-label={`Open ${row.districtName} in Schools and Districts`}
            component={RouterLink}
            sx={{ alignItems: "center", display: "inline-flex", gap: 0.5 }}
            to={`/admin?adminSelect=${row.bubbleId}`}
            underline="hover"
            variant="body2"
          >
            {row.districtName}
            <LinkIcon fontSize="inherit" sx={{ opacity: 0.7 }} />
          </MuiLink>
        ),
      },
      {
        field: "status",
        flex: 0.75,
        headerName: "Status",
        minWidth: 130,
        renderCell: ({ row }) => (
          <Chip color={statusColor(row.status)} label={row.status} size="small" variant="outlined" />
        ),
      },
      makeCountColumn("schoolCount", "Number of schools", 150, "schools", openDetail),
      makeCountColumn("schoolsOnboardedCount", "Number of schools onboarded", 220, "schoolsOnboarded", openDetail),
      makeCountColumn("peopleOnboardedCount", "Number of people onboarded", 220, "people", openDetail),
    ];
  }, [openDetail]);

  const rows = useMemo((): GridRow[] => buildDistrictOnboardingGridRows(data ?? undefined), [data]);

  return (
    <Box sx={{ maxWidth: "100%", minWidth: 0, pt: 1 }}>
      <Typography sx={{ mb: 0.5 }} variant="h6">
        Onboarding
      </Typography>
      <Typography color="text.secondary" sx={{ maxWidth: 900, mb: 2 }} variant="body2">
        Per-district view of schools and role-based participation. &quot;Number of people onboarded&quot; counts
        distinct users with at least one role on the district bubble or any school (or other descendant) bubble in that
        district&apos;s tree. &quot;Schools onboarded&quot; counts schools that have at least one such user in that
        school&apos;s bubble subtree. Status compares average people per school (district people divided by number of
        schools) to the thresholds below. Click a count in the last three columns to open a list of schools or people
        for that district.
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
        <strong>Not started:</strong> no people with roles in the district tree yet (people total is zero; schools alone
        do not change this). <strong>Onboarding:</strong> at least one school and average people per school is below 15.{" "}
        <strong>Launched:</strong> at least one school and that average is above 15. <strong>Starting:</strong> any
        other case (for example, average exactly 15, or people with only district-level roles and no schools listed).
      </Typography>
      <DataGrid<DistrictOnboardingGridRow>
        autoHeight
        columns={columns}
        disableRowSelectionOnClick
        rows={rows}
        status={status}
      />
      <DistrictOnboardingDetailDrawer
        district={detail?.district ?? null}
        metric={detail?.metric ?? null}
        onClose={closeDetail}
      />
    </Box>
  );
};
